import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, FileEdit, Send } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const empty = { situation: "", preparation: "", sections: [], required_documents: [], next_steps: [] };

const FamilyPlan = () => {
  const { caseId } = useParams();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const isConsultor = user?.role === "consultor" || user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/plan`);
      setPlan({ ...empty, ...data });
    } catch { toast.error("Erro ao carregar plano"); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (!plan) return <div className="p-10 text-slate-500">Carregando…</div>;

  const familyLockedView = user?.role === "familia" && !plan.released;

  const setField = (k, v) => setPlan({ ...plan, [k]: v });
  const setListItem = (list, i, v) => setPlan({ ...plan, [list]: plan[list].map((x, j) => j === i ? v : x) });
  const removeItem = (list, i) => setPlan({ ...plan, [list]: plan[list].filter((_, j) => j !== i) });
  const addItem = (list, empty) => setPlan({ ...plan, [list]: [...(plan[list] || []), empty] });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/cases/${caseId}/plan`, plan);
      toast.success("Plano salvo");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setBusy(false); }
  };

  const release = async () => {
    if (!window.confirm("Liberar o Plano de Direção Familiar para a Família ver? O caso avançará para PLANO_DIRECAO_FAMILIAR.")) return;
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/plan/release`);
      toast.success("Plano liberado para a Família");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="family-plan-page">
      <Link to={isConsultor ? `/consultor/caso/${caseId}` : `/caso/${caseId}`}
            className="text-sm text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Voltar ao caso
      </Link>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
          {plan.case_protocol ? `Protocolo ${plan.case_protocol}` : "Caso RAF"}
        </div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">Plano de Direção Familiar</h1>
        <p className="text-sm text-slate-600 mt-1">
          {isConsultor ? "Prepare a orientação e o próximo passo para a família." : "Explicação do caso e orientações preparadas pelo consultor RAF."}
        </p>
        {plan.released && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
            <CheckCircle2 className="w-3.5 h-3.5" /> Liberado para a Família
          </div>
        )}
      </div>

      {familyLockedView ? (
        <div className="p-8 bg-slate-50 border border-slate-200 rounded-xl text-center">
          <FileEdit className="w-8 h-8 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display font-semibold text-lg text-[#0B2B62]">Seu plano está sendo preparado</h3>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            {plan.message || "O consultor RAF está preparando o Plano de Direção Familiar para o seu caso. Você será avisado assim que estiver liberado."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <Section title="Situação atual" testid="plan-situation"
            value={plan.situation} onChange={(v) => setField("situation", v)}
            readOnly={!isConsultor} placeholder="Descreva de forma acolhedora a situação do caso e o que motiva o direcionamento." />

          <Section title="Preparação para o direcionamento" testid="plan-preparation"
            value={plan.preparation} onChange={(v) => setField("preparation", v)}
            readOnly={!isConsultor} placeholder="Orientações práticas para a família antes da admissão." />

          <ListBlock title="Documentos necessários" list="required_documents"
            items={plan.required_documents} isConsultor={isConsultor} testidPrefix="plan-doc"
            onAdd={() => addItem("required_documents", "")}
            onChange={(i, v) => setListItem("required_documents", i, v)}
            onRemove={(i) => removeItem("required_documents", i)} />

          <ListBlock title="Próximos passos" list="next_steps"
            items={plan.next_steps} isConsultor={isConsultor} testidPrefix="plan-step"
            onAdd={() => addItem("next_steps", "")}
            onChange={(i, v) => setListItem("next_steps", i, v)}
            onRemove={(i) => removeItem("next_steps", i)} />

          {isConsultor && (
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-200">
              <button data-testid="plan-save" onClick={save} disabled={busy}
                className="px-5 py-2.5 bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-50">
                Salvar rascunho
              </button>
              {!plan.released && (
                <button data-testid="plan-release" onClick={release} disabled={busy}
                  className="px-5 py-2.5 border-2 border-green-600 text-green-700 rounded-md font-semibold hover:bg-green-50 disabled:opacity-50 inline-flex items-center gap-2">
                  <Send className="w-4 h-4" /> Salvar e liberar para a Família
                </button>
              )}
              {plan.released && plan.released_at && (
                <span className="text-xs text-slate-500">Liberado em {new Date(plan.released_at).toLocaleString("pt-BR")}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, value, onChange, readOnly, placeholder, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    {readOnly ? (
      <p className="text-slate-700 whitespace-pre-line text-sm leading-relaxed">{value || <span className="text-slate-400">— não preenchido —</span>}</p>
    ) : (
      <textarea data-testid={testid} rows={4} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
    )}
  </div>
);

const ListBlock = ({ title, items = [], isConsultor, onAdd, onChange, onRemove, testidPrefix }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs uppercase tracking-widest text-slate-500">{title}</div>
      {isConsultor && (
        <button onClick={onAdd} data-testid={`${testidPrefix}-add`} className="text-xs text-[#0B2B62] font-semibold hover:underline">+ Adicionar</button>
      )}
    </div>
    {items.length === 0 && <div className="text-sm text-slate-400">— vazio —</div>}
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={`${testidPrefix}-${i}`} className="flex items-center gap-2">
          {isConsultor ? (
            <>
              <input data-testid={`${testidPrefix}-${i}`} value={it} onChange={(e) => onChange(i, e.target.value)}
                className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
              <button onClick={() => onRemove(i)} className="text-xs text-red-500 hover:text-red-700">Remover</button>
            </>
          ) : (
            <span className="text-sm text-slate-700">• {it}</span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default FamilyPlan;
